import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import { triggerBlobDownload } from "@thalamos/frontend";
import {
  CALENDAR_TIME_ZONE,
  type AssessmentId,
  type SharedAssessmentListing,
  type SharedWithGuest,
} from "@thalamos/haven-common";

import guestApi from "../../api/guest/api.js";
import { AssessmentKindChip } from "../../components/AssessmentKindChip/AssessmentKindChip.js";
import { guestRoute } from "../../routing/guestRoute.js";
import type { PdfDownloadOutcome } from "../Assessment/assessmentPdfDownload.js";
import { GuestBrandedPage } from "./GuestAssessmentLayout.js";

const { guest } = guestApi;

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

// The end of a UK calendar day, so it is shown as a UK date wherever the
// browser is.
const UK_CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "long",
  timeZone: CALENDAR_TIME_ZONE,
});

/**
 * Listing what a guest session's link shares: under way, listed, ended (the
 * session no longer admits the guest), or failed for another reason.
 */
export type SharedListing =
  | { kind: "reading" }
  | { kind: "listed"; shared: SharedWithGuest }
  | { kind: "ended" }
  | { kind: "failed" };

const listShared = async (): Promise<SharedListing> => {
  const shared = await guest.shared();

  if (shared.status === 401) {
    return { kind: "ended" };
  }

  return shared.data === undefined
    ? { kind: "failed" }
    : { kind: "listed", shared: shared.data };
};

/** Downloads one shared assessment's PDF, saving it under the name the server gave it. */
export const downloadShared = async (
  assessmentId: AssessmentId,
): Promise<PdfDownloadOutcome> => {
  const copy = await guest.sharedPdf(assessmentId).catch(() => undefined);

  if (copy === undefined) {
    return "failed";
  }

  triggerBlobDownload(copy);
  return "downloaded";
};

/**
 * Where a guest lands once signed in through a link that shares assessments:
 * what was shared, each to open or download.
 */
export const GuestSharedPage = () => {
  const [read, setRead] = useState<SharedListing>({ kind: "reading" });

  const readAgain = useCallback(async () => {
    setRead(
      await listShared().catch((): SharedListing => ({ kind: "failed" })),
    );
  }, []);

  useEffect(() => {
    void readAgain();
  }, [readAgain]);

  return (
    <GuestSharedContent
      read={read}
      onRetry={readAgain}
      onDownload={downloadShared}
    />
  );
};

/** One shared assessment as the list shows it, with Open and Download PDF. */
const SharedRow = ({
  listed,
  onDownload,
}: {
  listed: SharedAssessmentListing;
  onDownload: (assessmentId: AssessmentId) => Promisable<PdfDownloadOutcome>;
}) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);
  const { assessment, assessorName, headline } = listed;
  const finished = t(`guestShared.finished.${assessment.status}`, {
    date: CALENDAR_DATE.format(assessment.finishedAt),
  });

  return (
    <Stack spacing={1} sx={{ py: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <AssessmentKindChip definitive={assessment.definitive} />
        <Typography variant="subtitle1" component="h2">
          {finished}
        </Typography>
      </Stack>
      <Typography variant="body2">
        {assessorName === null
          ? t("guestShared.unknownAssessor")
          : t("guestShared.by", { assessor: assessorName })}
      </Typography>
      {headline === null || headline.length === 0 ? null : (
        <Typography variant="body2" color="text.secondary">
          {headline
            .map(({ category, count }) =>
              t(`guestShared.headline.${category}`, { count }),
            )
            .join(" · ")}
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          component={RouterLink}
          to={guestRoute.sharedAssessment(assessment.id)}
          aria-label={t("guestShared.openOf", { finished })}
        >
          {t("guestShared.open")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={downloading}
          aria-label={t("guestShared.downloadOf", { finished })}
          onClick={() => {
            setDownloading(true);
            setFailed(false);
            void Promise.resolve(assessment.id)
              .then(onDownload)
              .catch((): PdfDownloadOutcome => "failed")
              .then((outcome) => {
                setDownloading(false);
                setFailed(outcome === "failed");
              });
          }}
        >
          {t("guestShared.download")}
        </Button>
      </Stack>
      {failed ? (
        <Alert severity="error">{t("guestShared.downloadFailed")}</Alert>
      ) : null}
    </Stack>
  );
};

/**
 * The shared list's rendering: who shared and until when, a line on
 * definitive and non-definitive, and each assessment the guest may still
 * read, newest first.
 */
export const GuestSharedContent = ({
  read,
  onRetry,
  onDownload,
}: {
  read: SharedListing;
  onRetry: () => Promisable<void>;
  onDownload: (assessmentId: AssessmentId) => Promisable<PdfDownloadOutcome>;
}) => {
  const { t } = useTranslation();

  return (
    <GuestBrandedPage offersSignOut={read.kind !== "ended"}>
      {match(read)
        .with({ kind: "reading" }, () => (
          <Typography color="text.secondary">{t("common.loading")}</Typography>
        ))
        .with({ kind: "ended" }, () => (
          <Stack spacing={1}>
            <Typography variant="h4" component="h1">
              {t("guestAccess.sessionEnded.heading")}
            </Typography>
            <Typography>{t("guestAccess.sessionEnded.body")}</Typography>
          </Stack>
        ))
        .with({ kind: "failed" }, () => (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void onRetry()}
              >
                {t("guestShared.retry")}
              </Button>
            }
          >
            {t("guestShared.loadFailed")}
          </Alert>
        ))
        .with({ kind: "listed" }, ({ shared }) => (
          <Stack spacing={2}>
            <Typography variant="h4" component="h1">
              {t("guestShared.heading", {
                personFirstName: shared.personFirstName,
              })}
            </Typography>
            <Typography>
              {t("guestShared.sharedBy", {
                sharer: shared.sharerName,
                organisation: shared.organisationName,
              })}
            </Typography>
            {shared.expiresAt === null ? null : (
              <Typography>
                {t("guestShared.readUntil", {
                  date: UK_CALENDAR_DATE.format(shared.expiresAt),
                })}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t("guestShared.definitiveExplained")}
            </Typography>
            <Paper variant="outlined" sx={{ px: { xs: 2, sm: 3 } }}>
              {shared.assessments.length === 0 ? (
                <Typography sx={{ py: 2 }}>{t("guestShared.none")}</Typography>
              ) : (
                <Stack divider={<Divider />}>
                  {shared.assessments.map((listed) => (
                    <SharedRow
                      key={listed.assessment.id}
                      listed={listed}
                      onDownload={onDownload}
                    />
                  ))}
                </Stack>
              )}
            </Paper>
          </Stack>
        ))
        .exhaustive()}
    </GuestBrandedPage>
  );
};
